/**
 * The match-structure chips under the video's seek bar: KO, HT, 2H KO, FT.
 *
 * Each chip is centred on its time, so two marks a few seconds apart print
 * on top of each other. That is the normal case, not an edge one, when the
 * video file splices out the half-time break: HT and 2H KO land on the same
 * frame (PRI-1827). Two rules, so no seek point is lost that still exists:
 *
 *   - marks under SPLICE_S apart ARE one moment of video: one chip, seeking
 *     to the later mark (for HT + 2H KO, the restart);
 *   - marks merely close on the bar (under NEAR_FRACTION of the video) keep
 *     both chips, pushed apart — the earlier one ends at its time, the later
 *     one starts at its — so each still seeks to its own moment.
 */

export type TimelineMark = {
  t: number
  label: string
  title: string
  /** Which of the chip's edges sits on its time — the same anchors the
   *  bar's end clamps use (timeline-marks__chip--left/--right). Unset is
   *  centred. */
  anchor?: 'left' | 'right'
}

/** Marks this close are the same frame of a spliced video, give or take. */
export const SPLICE_S = 10

/** About one chip's width on a laptop-sized player. */
export const NEAR_FRACTION = 0.05

export function structureMarks(
  sync: {
    videoGameStartTime?: number | null
    videoHalftimeStartTime?: number | null
    videoHalftimeEndTime?: number | null
  },
  fullTime: number | null | undefined,
  videoDuration: number,
): TimelineMark[] {
  const marks: TimelineMark[] = []
  const gs = sync.videoGameStartTime
  const hs = sync.videoHalftimeStartTime
  const he = sync.videoHalftimeEndTime
  // 0 reads as unset for KO and FT: the ingest writes 0 for never measured.
  if (gs) marks.push({ t: gs, label: 'KO', title: 'Kickoff' })
  if (hs != null) marks.push({ t: hs, label: 'HT', title: 'End of first half' })
  if (he != null) marks.push({ t: he, label: '2H KO', title: 'Second-half kickoff' })
  if (fullTime) marks.push({ t: fullTime, label: 'FT', title: 'Full time' })

  const out: TimelineMark[] = []
  for (const m of [...marks].sort((a, b) => a.t - b.t)) {
    const prev = out[out.length - 1]
    const near = prev && videoDuration > 0 && (m.t - prev.t) / videoDuration < NEAR_FRACTION
    // A third mark inside one chip-width of a pushed-apart pair has no side
    // left to take; three structure marks that close mean mis-measured sync,
    // and it joins its neighbour's chip rather than print over it.
    if (prev && (m.t - prev.t < SPLICE_S || (near && prev.anchor === 'left'))) {
      // The chip seeks to the later mark, so its tooltip names that one.
      out[out.length - 1] = {
        t: m.t, label: `${prev.label} · ${m.label}`, title: m.title, anchor: prev.anchor,
      }
    } else if (prev && near) {
      out[out.length - 1] = { ...prev, anchor: 'right' }
      out.push({ ...m, anchor: 'left' })
    } else {
      out.push(m)
    }
  }
  return out
}
